import {
  Contact,
  ContactInput,
  ContactsResponse,
  ContactsKanbanResponse,
  MoveKanbanContactRequest,
  Category,
  CategoryInput,
  CategoriesResponse,
  Funnel,
  FunnelInput,
  PublicForm,
  ImportResult,
  ImportPreview,
  FollowUpConfig,
  FollowUpConfigInput,
  MarketingBrand,
  MarketingBrandInput,
  MarketingCreativeAsset,
  MarketingStudioDashboard,
  AgenticFunnelSuggestionResponse
} from '../types';
import type {
  RenderVideoEditorProjectResponse,
  SaveVideoEditorProjectResponse,
  VideoEditorProject,
  VideoEditorProjectResponse,
  VideoEditorAsset,
  VideoEditorSoundEffect,
  VideoEditorMusicTrack,
} from '../types/videoEditor';
import type { EdlRange, EdvidJobState, EdvidNote, EdvidVisualState, StyleSetup } from '../types/videoEdit';

const API_BASE_URL = '/api';

// Job do editor de vídeo visto pelo editor edvid: só o que ele lê. O resto do
// registro (input, stats, renders antigos) segue livre para o JobCard.
export interface EdvidJobRecord {
  id: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  currentStage?: string;
  createdAt?: string;
  input?: { originalFilenames?: string[]; options?: Record<string, unknown> } & Record<string, unknown>;
  output?: { edvid?: EdvidJobState } & Record<string, unknown>;
}

// Imagem que o criador anexa ao brief, com o comentário do que ele quer dela.
export interface BriefReferenceImage {
  url: string;
  note?: string | null;
}

// Roteiro FECHADO no chat do agente roteirista. É conteúdo travado: o gerador de
// brief vira diretor de arte, não roteirista — cada cena daqui vira exatamente um
// item de avatarSegments/takes/slides, com a fala literal.
export interface ChatRoteiroCena {
  ordem: number;
  fala: string;
  acao?: string;
  textoNaTela?: string;
}
export interface ChatRoteiro {
  titulo: string;
  hook: string;
  promessa?: string;
  cta?: string | null;
  cenas: ChatRoteiroCena[];
  duracaoAlvoSegundos?: number;
  observacoes?: string;
}

export interface ProviderCostRow {
  id: string;
  key: string;
  provider: string;
  label: string;
  unit: string;
  cost: number;
  currency: ProviderCurrency;
  markupOverride: number | null;
  effectiveFrom: string;
  active: boolean;
  current: boolean;    // linha vigente da chave agora
  scheduled: boolean;  // vigência futura
  costBrl: number;
  credits: number;     // créditos por unidade
}
export interface ProviderCostTable {
  settings: { usdBrl: number; markup: number; creditValueBrl: number };
  rates: ProviderCostRow[];
}
// CREDITO = linha que já é preço em créditos (mensalidade de conexão): sem câmbio nem markup.
export type ProviderCurrency = 'BRL' | 'USD' | 'CREDITO';

// Componente da receita de uma ação: linha de custo × quantidade, ou tokens médios na área de LLM.
export type PriceComponent =
  | { rate: string; qty: number }
  | { llm: string; in: number; cache?: number; out: number };
export interface PricedActionComponent {
  kind: 'rate' | 'llm';
  rate?: string; qty?: number; rateLabel?: string | null; rateUnit?: string | null; missing?: boolean;
  llm?: string; in?: number; cache?: number; out?: number; areaLabel?: string; model?: string;
  credits: number;
  costBrl: number;
}
export interface PricedAction {
  label: string;
  unit: string;
  group: 'crm' | 'leads' | 'seller' | 'voz' | 'marketing' | 'conexoes';
  recurring?: boolean;
  cost: number;      // créditos por unidade
  costBrl: number;   // custo de provedor em R$ por unidade
  components: PricedActionComponent[];
  defaultComponents: PriceComponent[];
  custom: boolean;
  legacyCost: number | null;
}
export interface LlmAreaRow {
  key: string;
  label: string;
  group: 'crm' | 'seller' | 'marketing' | 'criar_conteudo';
  hint: string;
  model: string;
  source: 'superadmin' | 'stack' | 'padrao';
  adminValue: string;
  defaultModel?: string;
  prefix: string;   // provedor escolhido para a área ('' = o do modelo)
}
export interface PricingTable extends ProviderCostTable {
  actions: Record<string, PricedAction>;
  areas: LlmAreaRow[];
  modelOptions: { id: string; family: string; label: string }[];
  familyOptions: { key: string; label: string }[];   // "Cobrar como" de um id livre
  modelFamilies: Record<string, string>;             // id livre → família de preço
}

export type CreativeSessionStatus = 'idle' | 'queued' | 'running' | 'waiting_approval' | 'interrupted' | 'error';
export interface CreativeSessionSummary {
  id: string;
  title: string;
  status: CreativeSessionStatus;
  creditsSpent: number | string;
  createdAt: string;
  updatedAt: string;
}
export interface CreativeEvent {
  id: number;
  type: 'user_message' | 'assistant_message' | 'activity' | 'plan' | 'budget' | 'publication' | 'ideas' | 'agent_switch' | 'memory' | 'notice' | 'error';
  payload: any;
  createdAt: string;
  updatedAt: string;
}
export interface CreativeBudgetRecord {
  id: string;
  lines: { key: string; label: string; unit: string; qty: number; unitCredits: number; credits: number; note: string | null }[];
  totalCredits: number;
  rationale: string | null;
  status: 'proposed' | 'approved' | 'rejected' | 'superseded';
  usedQty: Record<string, number>;
  consumedCredits: number;
  approvedAt: string | null;
  approvedBy: string | null;
}
export interface CreativeMemoryItem {
  id: string;
  brandId: string | null;
  category: string;
  content: string;
  /** user: dito no chat · inferred: a IA percebeu · manual: escrito na tela */
  source: 'user' | 'inferred' | 'manual';
  sessionId: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreativeDeliverable {
  id: string;
  kind: 'carousel' | 'post' | 'story' | 'reel';
  title: string;
  size: string | null;
  assets: { index: number; url?: string; type?: 'image' | 'video'; words?: number; issues?: { type: string; severity: 'error' | 'warning'; message: string }[] }[];
  coverIndex: number;
  review: string | null;
  caption: string | null;
  hashtags: string[];
  status: 'draft' | 'scheduled' | 'published';
  scheduledPostId: string | null;
  updatedAt: string;
}
export interface CreativeSessionState {
  session: CreativeSessionSummary & { lastError: string | null; brandId: string | null; activeAgent?: 'ideacao' | 'producao' };
  events: CreativeEvent[];
  budgets: CreativeBudgetRecord[];
  balance: number;
  deliverables: CreativeDeliverable[];
  serverTime: string;
  /** Só na carga paginada: existem eventos mais antigos que os devolvidos. */
  hasMore?: boolean;
}

export type CreditPurchaseStatus = 'CREATING' | 'PENDING' | 'PAID' | 'FAILED' | 'EXPIRED' | 'REFUNDED' | 'CHARGEBACK';
export interface CreditPurchase {
  id: string;
  credits: number;
  amountCents: number;
  billingType: 'PIX' | 'CREDIT_CARD';
  status: CreditPurchaseStatus;
  providerStatus?: string | null;
  pix?: { payload: string; encodedImage: string; expirationDate?: string | null } | null;
  failureMessage?: string | null;
  creditedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

// ===== Cut App (integracao de produtos e precos) =====
// Resultado do sync de produtos de marketplace. `vinculosRemovidos`/`produtosRemovidos`
// vêm da poda de anúncios excluídos: só são preenchidos quando a varredura foi íntegra.
export interface MarketplaceProductSyncResult {
  success: boolean;
  products: any[];
  created: number;
  updated: number;
  skipped: number;
  total: number;
  vinculosRemovidos?: Array<{ productId?: string; sku?: string; marketplace?: string; listingId?: string; variationId?: string | null; motivo?: string }>;
  produtosRemovidos?: Array<{ productId?: string; sku?: string; nome?: string; motivo?: string; listingIds?: string[] }>;
  poda?: { executada?: boolean; motivo?: string | null; pendencias?: any[] };
}

export type CutappPhotoMode = 'skip' | 'overwrite';

// Espelha o opt-in normalizado pelo backend (cutappSyncService). `sku` NAO e opcional:
// e a chave de casamento do §11.4 e sempre gravado.
export interface CutappFieldOptions {
  name?: boolean;
  description?: boolean;
  category?: boolean;
  dimensions?: boolean;
  fiscal?: boolean;
  prices?: boolean;
}

export interface CutappConnectionInput {
  baseUrl?: string;
  apiKey?: string;
  /** A ordem do array E a prioridade de sobrescrita: o ultimo prevalece. */
  catalogIds?: string[];
  fieldOptions?: CutappFieldOptions;
  photoMode?: CutappPhotoMode;
  syncEnabled?: boolean;
}

export interface CutappConnection extends CutappConnectionInput {
  id?: string;
  apiKeyConfigured?: boolean;
  /** O backend so informa SE existe chave salva — nunca devolve a chave. */
  hasApiKey?: boolean;
  lastSyncAt?: string | null;
}

export interface CutappCatalog {
  id: string;
  nome?: string;
  name?: string;
}

export interface CutappDuplicatedSku {
  sku: string;
  catalogos: string[];
}

export interface CutappRounding {
  mode: 'none' | 'floor' | 'floor90' | 'floor99';
  step?: number;
}

export interface CutappPriceMapping {
  marketplace: 'shopee' | 'mercadolivre';
  basePriceFrom: 'sugerido' | 'valor' | 'precoFinal';
  campaignPriceFrom: 'valor' | 'sugerido';
  /** Desconto sobre o PRECO DE CAMPANHA (o `valor` do Cut App). Minimo 10. */
  flashDiscountPct: number | string | null;
  /** 'manual': Minimo = Campanha x (1 - %). 'cutapp_minimo': Minimo = preco minimo que o Cut App calcula com a margem minima do produto. */
  flashDiscountMode?: 'manual' | 'cutapp_minimo';
  rounding: CutappRounding;
  enabled: boolean;
}

class ApiService {
  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    const token = localStorage.getItem('auth_token');

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options?.headers,
    };

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }
    const selectedTenantId = localStorage.getItem('superadmin_selected_tenant') || localStorage.getItem('selected_tenant_id');
    if (selectedTenantId) (headers as Record<string, string>)['X-Tenant-Id'] = selectedTenantId;

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Erro desconhecido' }));

      // Detectar erro de créditos insuficientes (HTTP 402) e sinalizar para a UI
      if (response.status === 402 || error.code === 'INSUFFICIENT_CREDITS') {
        const required = error.required;
        const balance = error.balance;
        const msg = error.message || error.error
          || (required != null
            ? `Créditos insuficientes: esta ação requer ${required} e seu saldo é ${balance ?? 0}.`
            : 'Créditos insuficientes.');
        const creditsError = new Error(msg);
        (creditsError as any).isCreditsError = true;
        (creditsError as any).required = required;
        (creditsError as any).balance = balance;
        (creditsError as any).featureKey = error.featureKey;
        throw creditsError;
      }

      // Detectar erro de quota e adicionar flag para tratamento especial
      if (error.upgradeRequired || (error.message && error.message.includes('Limite'))) {
        const quotaError = new Error(error.message || error.error || 'Limite atingido');
        (quotaError as any).isQuotaError = true;
        (quotaError as any).upgradeRequired = true;
        throw quotaError;
      }

      const requestError = new Error(error.error || error.message || `HTTP ${response.status}`);
      Object.assign(requestError, error, { status: response.status });
      throw requestError;
    }

    // Handle empty responses (like 204 No Content)
    if (response.status === 204 || response.headers.get('content-length') === '0') {
      return {} as T;
    }

    return response.json();
  }

  private async requestRaw<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    const token = localStorage.getItem('auth_token');
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    const selectedTenantId = localStorage.getItem('superadmin_selected_tenant') || localStorage.getItem('selected_tenant_id');
    if (selectedTenantId) headers['X-Tenant-Id'] = selectedTenantId;
    const response = await fetch(url, { ...options, headers });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
      // Mesmo tratamento de 402 do request() — os envios multipart (upload de
      // vídeo, por exemplo) também precisam sinalizar créditos insuficientes
      // para a UI abrir o fluxo de compra em vez de um erro genérico.
      if (response.status === 402 || error.code === 'INSUFFICIENT_CREDITS') {
        const creditsError = new Error(error.message || error.error
          || (error.required != null
            ? `Créditos insuficientes: esta ação requer ${error.required} e seu saldo é ${error.balance ?? 0}.`
            : 'Créditos insuficientes.'));
        (creditsError as any).isCreditsError = true;
        (creditsError as any).required = error.required;
        (creditsError as any).balance = error.balance;
        (creditsError as any).featureKey = error.featureKey;
        throw creditsError;
      }
      throw new Error(error.error || error.message || `HTTP ${response.status}`);
    }
    return response.json();
  }

  // Tabela central de preços de créditos (para exibir custo antes de executar)
  async getCreditPricing(): Promise<{ success: boolean; data: Record<string, { cost: number; unit: string; label: string; recurring?: boolean }> }> {
    return this.request('/credits/pricing');
  }

  // SUPERADMIN: tabela única de preços
  // Receita da ação (componentes sobre a tabela de custos)
  async updateCreditPricing(featureKey: string, components: PriceComponent[]): Promise<{ success: boolean; data: PricedAction }> {
    return this.request(`/credits/admin/pricing/${encodeURIComponent(featureKey)}`, {
      method: 'PUT',
      body: JSON.stringify({ components }),
    });
  }
  // SUPERADMIN: tabela única (parâmetros, custos, ações e modelos por área)
  async getPricingTable(): Promise<{ success: boolean; data: PricingTable }> {
    return this.request('/credits/admin/pricing-table');
  }
  async updateLlmAreas(models: Record<string, string>, prefixes?: Record<string, string>, families?: Record<string, string>): Promise<{ success: boolean; data: { areas: LlmAreaRow[]; modelFamilies: Record<string, string> } }> {
    return this.request('/credits/admin/llm-areas', { method: 'PUT', body: JSON.stringify({ models, prefixes, families }) });
  }
  async resetCreditPricing(featureKey: string): Promise<{ success: boolean; data: any }> {
    return this.request(`/credits/admin/pricing/${encodeURIComponent(featureKey)}`, {
      method: 'DELETE',
    });
  }

  // SUPERADMIN: custos de provedor do Criar Conteúdo IA (custo → créditos)
  async getProviderCosts(): Promise<{ success: boolean; data: ProviderCostTable }> {
    return this.request('/credits/admin/provider-costs');
  }
  async updateProviderCostSettings(settings: Partial<ProviderCostTable['settings']>): Promise<{ success: boolean; data: ProviderCostTable }> {
    return this.request('/credits/admin/provider-costs/settings', { method: 'PUT', body: JSON.stringify(settings) });
  }
  async updateProviderCostRate(id: string, data: { cost?: number; currency?: ProviderCurrency; markupOverride?: number | null; active?: boolean }): Promise<{ success: boolean; data: ProviderCostTable }> {
    return this.request(`/credits/admin/provider-costs/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(data) });
  }
  async scheduleProviderCostRate(key: string, data: { cost: number; currency?: ProviderCurrency; effectiveFrom?: string }): Promise<{ success: boolean; data: ProviderCostTable }> {
    return this.request(`/credits/admin/provider-costs/${encodeURIComponent(key)}/schedule`, { method: 'POST', body: JSON.stringify(data) });
  }
  // Orçamento em créditos (Criar Conteúdo IA)
  async quoteProviderCosts(lines: { key: string; qty: number; note?: string }[]): Promise<{ success: boolean; data: { lines: any[]; totalCredits: number } }> {
    return this.request('/credits/provider-costs/quote', { method: 'POST', body: JSON.stringify({ lines }) });
  }

  // Criar Conteúdo IA — agente Diretor Criativo (roda no backend; a tela lê a linha do tempo)
  async listCreativeSessions(): Promise<{ success: boolean; data: CreativeSessionSummary[] }> {
    return this.request('/marketing-studio/creative/sessions');
  }
  async createCreativeSession(brandId?: string | null): Promise<{ success: boolean; data: CreativeSessionSummary }> {
    return this.request('/marketing-studio/creative/sessions', { method: 'POST', body: JSON.stringify({ brandId: brandId || null }) });
  }
  async getCreativeSession(sessionId: string, since?: string | null, page?: { before?: number; limit?: number }): Promise<{ success: boolean; data: CreativeSessionState }> {
    const params = new URLSearchParams();
    if (since) params.set('since', since);
    if (page?.before) params.set('before', String(page.before));
    if (page?.limit) params.set('limit', String(page.limit));
    const qs = params.toString() ? `?${params}` : '';
    return this.request(`/marketing-studio/creative/sessions/${encodeURIComponent(sessionId)}${qs}`);
  }
  async deleteCreativeSession(sessionId: string): Promise<{ success: boolean }> {
    return this.request(`/marketing-studio/creative/sessions/${encodeURIComponent(sessionId)}`, { method: 'DELETE' });
  }
  // agent: fixa o agente (Ideação ou Produção); sem ele o backend roteia pela mensagem.
  // Com anexos vai como multipart por XHR: o fetch não informa o progresso do envio, e vídeo do
  // usuário pode ter centenas de MB. `opts.onProgress(enviado, total)` alimenta a barra;
  // `opts.signal` cancela o envio.
  async sendCreativeMessage(
    sessionId: string, text: string, files: File[] = [], agent?: 'ideacao' | 'producao',
    opts: { onProgress?: (loaded: number, total: number) => void; signal?: AbortSignal } = {},
  ): Promise<{ success: boolean }> {
    const endpoint = `/marketing-studio/creative/sessions/${encodeURIComponent(sessionId)}/messages`;
    if (!files.length) return this.request(endpoint, { method: 'POST', body: JSON.stringify({ text, ...(agent ? { agent } : {}) }) });
    const form = new FormData();
    form.append('text', text);
    if (agent) form.append('agent', agent);
    files.forEach(f => form.append('files', f, f.name));
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_BASE_URL}${endpoint}`);
      const token = localStorage.getItem('auth_token');
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      const selectedTenantId = localStorage.getItem('superadmin_selected_tenant') || localStorage.getItem('selected_tenant_id');
      if (selectedTenantId) xhr.setRequestHeader('X-Tenant-Id', selectedTenantId);
      xhr.upload.onprogress = (e) => { if (e.lengthComputable) opts.onProgress?.(e.loaded, e.total); };
      xhr.onload = () => {
        let body: any = {};
        try { body = JSON.parse(xhr.responseText || '{}'); } catch { /* resposta sem JSON */ }
        if (xhr.status >= 200 && xhr.status < 300) return resolve(body);
        if (xhr.status === 413) return reject(new Error('Arquivo grande demais para o servidor.'));
        if (xhr.status === 402 || body.code === 'INSUFFICIENT_CREDITS') {
          const err: any = new Error(body.message || body.error || 'Créditos insuficientes.');
          err.isCreditsError = true; err.required = body.required; err.balance = body.balance; err.featureKey = body.featureKey;
          return reject(err);
        }
        reject(new Error(body.error || body.message || `HTTP ${xhr.status}`));
      };
      xhr.onerror = () => reject(new Error('A conexão caiu durante o envio. Tente de novo.'));
      xhr.onabort = () => reject(Object.assign(new Error('Envio cancelado.'), { aborted: true }));
      if (opts.signal) {
        if (opts.signal.aborted) { xhr.abort(); return; }
        opts.signal.addEventListener('abort', () => xhr.abort(), { once: true });
      }
      xhr.send(form);
    });
  }
  async decideCreativeBudget(sessionId: string, budgetId: string, decision: 'approve' | 'reject', reason?: string, option?: 'recomendado' | 'economico'): Promise<{ success: boolean }> {
    return this.request(`/marketing-studio/creative/sessions/${encodeURIComponent(sessionId)}/budgets/${encodeURIComponent(budgetId)}/${decision}`, {
      method: 'POST',
      body: JSON.stringify({ ...(reason ? { reason } : {}), ...(option ? { option } : {}) }),
    });
  }
  async decideCreativePublication(sessionId: string, publicationId: string, decision: 'approve' | 'reject', extra: { caption?: string; tiktokCaption?: string; reason?: string; aiLabel?: boolean } = {}): Promise<{ success: boolean }> {
    return this.request(`/marketing-studio/creative/sessions/${encodeURIComponent(sessionId)}/publications/${encodeURIComponent(publicationId)}/${decision}`, {
      method: 'POST',
      body: JSON.stringify(extra),
    });
  }
  async stopCreativeSession(sessionId: string): Promise<{ success: boolean }> {
    return this.request(`/marketing-studio/creative/sessions/${encodeURIComponent(sessionId)}/stop`, { method: 'POST' });
  }
  async getReferenceLibrary(q?: string): Promise<{ success: boolean; data: { profiles: any[]; posts: any[] } }> {
    return this.request(`/marketing-studio/creative/library${q ? `?q=${encodeURIComponent(q)}` : ''}`);
  }
  async getReferencePost(postId: string): Promise<{ success: boolean; data: any }> {
    return this.request(`/marketing-studio/creative/library/posts/${encodeURIComponent(postId)}`);
  }
  async deleteReferenceItem(kind: 'posts' | 'profiles', id: string): Promise<{ success: boolean }> {
    return this.request(`/marketing-studio/creative/library/${kind}/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }
  // Memória da marca (Criar Conteúdo)
  async listCreativeMemory(brandId: string | null): Promise<{ success: boolean; data: { items: CreativeMemoryItem[]; settings: { autoLearn: boolean }; categories: Record<string, string> } }> {
    return this.request(`/marketing-studio/creative/memory${brandId ? `?brandId=${encodeURIComponent(brandId)}` : ''}`);
  }
  async createCreativeMemory(brandId: string | null, category: string, content: string): Promise<{ success: boolean; data: CreativeMemoryItem }> {
    return this.request('/marketing-studio/creative/memory', { method: 'POST', body: JSON.stringify({ brandId, category, content }) });
  }
  async updateCreativeMemory(id: string, patch: { category?: string; content?: string; active?: boolean }): Promise<{ success: boolean; data: CreativeMemoryItem }> {
    return this.request(`/marketing-studio/creative/memory/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(patch) });
  }
  async deleteCreativeMemory(id: string): Promise<{ success: boolean }> {
    return this.request(`/marketing-studio/creative/memory/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }
  async setCreativeMemorySettings(brandId: string | null, autoLearn: boolean): Promise<{ success: boolean; data: { autoLearn: boolean } }> {
    return this.request('/marketing-studio/creative/memory-settings', { method: 'PUT', body: JSON.stringify({ brandId, autoLearn }) });
  }
  async resumeCreativeSession(sessionId: string): Promise<{ success: boolean }> {
    return this.request(`/marketing-studio/creative/sessions/${encodeURIComponent(sessionId)}/resume`, { method: 'POST' });
  }

  // SUPERADMIN: telemetria de geração de IA (Marketing Studio + e-commerce)
  async getGenerationLogs(params: Record<string, string | number | boolean | undefined>): Promise<{ success: boolean; data: { total: number; limit: number; offset: number; items: any[] } }> {
    const qs = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value === undefined || value === '' || value === 'all') continue;
      qs.set(key, String(value));
    }
    const q = qs.toString();
    return this.request(`/generation-logs${q ? `?${q}` : ''}`);
  }
  async getGenerationLogStats(params: Record<string, string | number | boolean | undefined>): Promise<{ success: boolean; data: any }> {
    const qs = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value === undefined || value === '' || value === 'all') continue;
      qs.set(key, String(value));
    }
    const q = qs.toString();
    return this.request(`/generation-logs/stats${q ? `?${q}` : ''}`);
  }
  async getGenerationLogFilters(): Promise<{ success: boolean; data: any }> {
    return this.request('/generation-logs/filters');
  }
  async getGenerationLogDetail(id: string): Promise<{ success: boolean; data: any }> {
    return this.request(`/generation-logs/${encodeURIComponent(id)}`);
  }
  async runGenerationLogMaintenance(): Promise<{ success: boolean; data: { reconciled: number; purged: number } }> {
    return this.request('/generation-logs/maintenance', { method: 'POST' });
  }

  // Estimativa (teto) de itens que seriam cobrados ao rodar os agentes do Seller
  async estimateSellerAnalysis(params?: { marketplace?: string; connectionId?: string }): Promise<{ success: boolean; count: number }> {
    const qs = new URLSearchParams();
    if (params?.marketplace) qs.set('marketplace', params.marketplace);
    if (params?.connectionId) qs.set('connectionId', params.connectionId);
    const q = qs.toString();
    return this.request(`/seller/analysis/estimate${q ? `?${q}` : ''}`);
  }
  async estimateSellerAdsAgent(params?: { marketplace?: string; connectionId?: string }): Promise<{ success: boolean; count: number }> {
    const qs = new URLSearchParams();
    if (params?.marketplace) qs.set('marketplace', params.marketplace);
    if (params?.connectionId) qs.set('connectionId', params.connectionId);
    const q = qs.toString();
    return this.request(`/seller/ads-agent/estimate${q ? `?${q}` : ''}`);
  }

  async getContacts(params?: {
    search?: string;
    tag?: string;
    label?: string;
    page?: number;
    pageSize?: number;
  }): Promise<ContactsResponse> {
    const searchParams = new URLSearchParams();

    if (params?.search) searchParams.set('search', params.search);
    if (params?.tag) searchParams.set('tag', params.tag);
    if (params?.label) searchParams.set('label', params.label);
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());

    const queryString = searchParams.toString();
    const endpoint = `/contatos${queryString ? `?${queryString}` : ''}`;

    return this.request<ContactsResponse>(endpoint);
  }

  async getContactsKanban(params?: {
    funnelId?: string;
    columnId?: string;
    offset?: number;
    pageSize?: number;
    label?: string;
  }): Promise<ContactsKanbanResponse> {
    const searchParams = new URLSearchParams();
    if (params?.funnelId) searchParams.set('funnelId', params.funnelId);
    if (params?.columnId) searchParams.set('columnId', params.columnId);
    if (params?.offset !== undefined) searchParams.set('offset', String(params.offset));
    if (params?.pageSize) searchParams.set('pageSize', String(params.pageSize));
    if (params?.label) searchParams.set('label', params.label);
    const queryString = searchParams.toString();
    const endpoint = `/contatos/kanban${queryString ? `?${queryString}` : ''}`;
    return this.request<ContactsKanbanResponse>(endpoint);
  }

  async moveKanbanContact(data: MoveKanbanContactRequest): Promise<{ id: string; categoriaId: string | null; position: number }> {
    return this.request('/contatos/kanban/move', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async getContactLabels(): Promise<import('../types').ContactLabel[]> {
    return this.request('/contatos/labels/all');
  }

  async createContactLabel(data: { name: string; color: string }): Promise<import('../types').ContactLabel> {
    return this.request('/contatos/labels', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateContactLabel(id: string, data: { name: string; color: string }): Promise<import('../types').ContactLabel> {
    return this.request(`/contatos/labels/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  async deleteContactLabel(id: string): Promise<void> {
    return this.request(`/contatos/labels/${id}`, { method: 'DELETE' });
  }

  async resolveConversationForContact(contactId: string, externalAccountId?: string): Promise<{
    success: boolean;
    data: {
      status: 'existing' | 'created' | 'choose_inbox' | 'no_inbox' | 'no_phone' | 'not_on_whatsapp';
      conversationId?: string;
      inboxes?: { externalAccountId: string; name: string }[];
    };
  }> {
    return this.request('/crm-chat/conversations/resolve-for-contact', {
      method: 'POST',
      body: JSON.stringify({ contactId, ...(externalAccountId ? { externalAccountId } : {}) })
    });
  }

  // Inicia (ou reabre) uma conversa a partir de um contactId OU de um telefone digitado.
  async startCrmConversation(params: { contactId?: string; phone?: string; externalAccountId?: string }): Promise<{
    success: boolean;
    data: {
      status: 'existing' | 'created' | 'choose_inbox' | 'no_inbox' | 'no_phone' | 'not_on_whatsapp';
      conversationId?: string;
      inboxes?: { externalAccountId: string; name: string }[];
    };
  }> {
    return this.request('/crm-chat/conversations/resolve-for-contact', {
      method: 'POST',
      body: JSON.stringify(params)
    });
  }

  async getContact(id: string): Promise<Contact> {
    return this.request<Contact>(`/contatos/${id}`);
  }

  async createContact(data: ContactInput): Promise<Contact> {
    return this.request<Contact>('/contatos', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateContact(id: string, data: ContactInput): Promise<Contact> {
    return this.request<Contact>(`/contatos/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteContact(id: string): Promise<void> {
    await this.request<void>(`/contatos/${id}`, {
      method: 'DELETE',
    });
  }

  // Category methods
  async getCategories(params?: {
    search?: string;
    page?: number;
    pageSize?: number;
  }): Promise<CategoriesResponse> {
    const searchParams = new URLSearchParams();

    if (params?.search) searchParams.set('search', params.search);
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());

    const queryString = searchParams.toString();
    const endpoint = `/categorias${queryString ? `?${queryString}` : ''}`;

    return this.request<CategoriesResponse>(endpoint);
  }

  async getAllCategories(): Promise<Category[]> {
    return this.request<Category[]>('/categorias/all');
  }

  async getLabelerVisibleCategories(): Promise<Category[]> {
    return this.request<Category[]>('/categorias/labeler-visible');
  }

  async getCategory(id: string): Promise<Category> {
    return this.request<Category>(`/categorias/${id}`);
  }

  async createCategory(data: CategoryInput): Promise<Category> {
    return this.request<Category>('/categorias', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCategory(id: string, data: CategoryInput): Promise<Category> {
    return this.request<Category>(`/categorias/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async updateCategoryLabelerVisibility(id: string, visibleToLabeler: boolean): Promise<Category> {
    return this.request<Category>(`/categorias/${id}/labeler-visibility`, {
      method: 'PATCH',
      body: JSON.stringify({ visibleToLabeler })
    });
  }

  async deleteCategory(id: string): Promise<void> {
    await this.request<void>(`/categorias/${id}`, {
      method: 'DELETE',
    });
  }

  async aiRewriteCategoryField(data: { currentText: string; instruction: string; field: 'descricao' | 'prompt' }): Promise<{ rewrittenText: string }> {
    return this.request<{ rewrittenText: string }>('/categorias/ai-rewrite', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Funnel methods
  async getPersonalizationOptions() {
    return this.request<import('../types/personalization').PersonalizationOptions>('/funis/personalization/options');
  }
  async getShopeeExtensionToken(connectionId: string) {
    return this.request<{ token: string; expiresInDays: number }>(`/shopee/connections/${encodeURIComponent(connectionId)}/extension-token`, { method: 'POST' });
  }
  async finishMarketplacePersonalization(conversationId: string) {
    return this.request(`/crm-chat/conversations/${encodeURIComponent(conversationId)}/personalization/finish`, { method: 'POST' });
  }
  async getFunnelPersonalization(id: string) {
    return this.request<import('../types/personalization').PersonalizationConfig | null>(`/funis/${encodeURIComponent(id)}/personalization`);
  }
  async setFunnelMarketplaceMode(id: string, enabled: boolean): Promise<Funnel> {
    return this.request(`/funis/${encodeURIComponent(id)}/marketplace-mode`, { method: 'PUT', body: JSON.stringify({ enabled }) });
  }
  async saveFunnelPersonalization(id: string, data: import('../types/personalization').PersonalizationConfig) {
    return this.request(`/funis/${encodeURIComponent(id)}/personalization`, { method: 'PUT', body: JSON.stringify(data) });
  }
  async getPersonalizationProducts(connectionId: string, offset = 0) {
    return this.request<{ products: { id: string; name: string; image: string | null }[]; nextOffset: number | null }>(`/funis/personalization/products/${encodeURIComponent(connectionId)}?offset=${offset}`);
  }
  async getFunnels(params?: { includeCategories?: boolean }): Promise<Funnel[]> {
    const searchParams = new URLSearchParams();
    if (params?.includeCategories) {
      searchParams.set('includeCategories', 'true');
    }
    const queryString = searchParams.toString();
    const endpoint = `/funis${queryString ? `?${queryString}` : ''}`;
    return this.request<Funnel[]>(endpoint);
  }

  async getFunnel(id: string): Promise<Funnel> {
    return this.request<Funnel>(`/funis/${id}`);
  }

  async createFunnel(data: FunnelInput): Promise<Funnel> {
    return this.request<Funnel>('/funis', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateFunnel(id: string, data: FunnelInput): Promise<Funnel> {
    return this.request<Funnel>(`/funis/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteFunnel(id: string): Promise<void> {
    await this.request<void>(`/funis/${id}`, {
      method: 'DELETE',
    });
  }

  async cloneFunnel(id: string): Promise<Funnel> {
    return this.request<Funnel>(`/funis/${id}/clone`, {
      method: 'POST',
    });
  }

  async updateFunnelCategories(id: string, categories: Array<{ id: string; funnelOrder?: number | null; funnelId?: string | null }>): Promise<void> {
    await this.request(`/funis/${id}/categories`, {
      method: 'POST',
      body: JSON.stringify({ categories }),
    });
  }

  async suggestAgenticFunnelCategories(data: Record<string, unknown>): Promise<AgenticFunnelSuggestionResponse> {
    return this.request<AgenticFunnelSuggestionResponse>('/funis/agentic/suggest-categories', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async prepareAgenticFunnelStep(step: string, data: Record<string, unknown>): Promise<Record<string, any>> {
    return this.request<Record<string, any>>('/funis/agentic/prepare-step', {
      method: 'POST',
      body: JSON.stringify({ step, data }),
    });
  }

  async createAgenticFunnel(data: Record<string, unknown>): Promise<Funnel> {
    return this.request<Funnel>('/funis/agentic/create', { method: 'POST', body: JSON.stringify(data) });
  }
  async prepareMarketplaceFunnel(data: Record<string, unknown>): Promise<import('../components/MarketplaceFunnelWizard').MarketplaceFunnelDraft> {
    return this.request('/funis/marketplace/prepare', { method: 'POST', body: JSON.stringify(data) });
  }
  async createMarketplaceFunnel(data: Record<string, unknown>): Promise<Funnel> {
    return this.request('/funis/marketplace/create', { method: 'POST', body: JSON.stringify(data) });
  }

  async listForms(): Promise<PublicForm[]> {
    return this.request<PublicForm[]>('/forms');
  }

  async createForm(data: Partial<PublicForm>): Promise<PublicForm> {
    return this.request<PublicForm>('/forms', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateForm(id: string, data: Partial<PublicForm>): Promise<PublicForm> {
    return this.request<PublicForm>(`/forms/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  async deleteForm(id: string): Promise<void> {
    await this.request<void>(`/forms/${id}`, { method: 'DELETE' });
  }

  async getPublicForm(slug: string): Promise<PublicForm> {
    return this.request<PublicForm>(`/forms/public/${slug}`);
  }

  async getFormSubmissions(id: string): Promise<{ fields: any[]; submissions: any[] }> {
    return this.request(`/forms/${id}/submissions`);
  }

  async submitPublicForm(slug: string, answers: Record<string, string>): Promise<{ success: boolean; message: string; redirectUrl?: string | null }> {
    return this.request(`/forms/public/${slug}/submit`, { method: 'POST', body: JSON.stringify({ answers }) });
  }

  // CSV Import methods
  async importCSV(file: File, updateExisting = false): Promise<ImportResult> {
    const formData = new FormData();
    formData.append('csv', file);

    const token = localStorage.getItem('auth_token');
    const headers: HeadersInit = {};

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    const url = updateExisting
      ? `${API_BASE_URL}/csv/import?updateExisting=true`
      : `${API_BASE_URL}/csv/import`;

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  async previewCSV(file: File): Promise<ImportPreview> {
    const formData = new FormData();
    formData.append('csv', file);

    const token = localStorage.getItem('auth_token');
    const headers: HeadersInit = {};

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE_URL}/csv/preview`, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  async downloadCSVTemplate(): Promise<Blob> {
    const token = localStorage.getItem('auth_token');
    const headers: HeadersInit = {};

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE_URL}/csv/template`, {
      headers
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    return response.blob();
  }

  async exportContactsCSV(params?: { search?: string; tag?: string }): Promise<Blob> {
    const searchParams = new URLSearchParams();
    if (params?.search) searchParams.set('search', params.search);
    if (params?.tag) searchParams.set('tag', params.tag);

    const token = localStorage.getItem('auth_token');
    const headers: HeadersInit = {};

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    const queryString = searchParams.toString();
    const response = await fetch(`${API_BASE_URL}/contatos/export/csv${queryString ? `?${queryString}` : ''}`, {
      headers
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    return response.blob();
  }

  // Test Chat methods
  async testChatMessage(data: {
    sessionId: string;
    message: string;
    categoryId?: string | null;
    skipLabeler?: boolean;
  }): Promise<any> {
    return this.request('/test-chat/message', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async testChatAssistantMessage(data: {
    sessionId: string;
    message: string;
    categoryId?: string | null;
  }): Promise<any> {
    return this.request('/test-chat/assistant-message', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async testChatClear(sessionId?: string): Promise<any> {
    return this.request('/test-chat/clear', {
      method: 'POST',
      body: JSON.stringify({ sessionId })
    });
  }

  async testChatSession(sessionId: string): Promise<any> {
    return this.request(`/test-chat/session/${sessionId}`);
  }

  async testChatCategories(): Promise<any[]> {
    return this.request('/test-chat/categories');
  }

  // Bulk operations
  async post(endpoint: string, data: any): Promise<any> {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Follow-up methods
  async getFollowUpConfig(): Promise<FollowUpConfig> {
    return this.request<FollowUpConfig>('/follow-up/config');
  }

  async updateFollowUpConfig(data: FollowUpConfigInput): Promise<FollowUpConfig> {
    return this.request<FollowUpConfig>('/follow-up/config', {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async getFollowUpContacts(params?: {
    followUpAtivo?: boolean;
    categoriaId?: string;
    search?: string;
  }): Promise<Contact[]> {
    const searchParams = new URLSearchParams();

    if (params?.followUpAtivo !== undefined) {
      searchParams.set('followUpAtivo', params.followUpAtivo.toString());
    }
    if (params?.categoriaId) {
      searchParams.set('categoriaId', params.categoriaId);
    }
    if (params?.search) {
      searchParams.set('search', params.search);
    }

    const queryString = searchParams.toString();
    const endpoint = `/follow-up/contatos${queryString ? `?${queryString}` : ''}`;

    return this.request<Contact[]>(endpoint);
  }

  async dispararFollowUpManual(contactId: string): Promise<{ success: boolean; message: string }> {
    return this.request(`/follow-up/disparar/${contactId}`, {
      method: 'POST'
    });
  }

  // =========================================================================
  // WhatsApp Templates (Late API)
  // =========================================================================

  async getWhatsAppAccounts(): Promise<any> {
    return this.request('/whatsapp-templates/accounts');
  }

  async getWhatsAppTemplates(accountId: string): Promise<any> {
    return this.request(`/whatsapp-templates/templates?accountId=${accountId}`);
  }

  async createWhatsAppTemplate(data: {
    accountId: string;
    name: string;
    category: string;
    language: string;
    components: any[];
    variableOrder?: string[];
  }): Promise<any> {
    return this.request('/whatsapp-templates/templates', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  // Sobe o arquivo de exemplo do cabeçalho para o nosso MinIO e devolve a URL
  // pública — é ela que a Meta baixa para revisar o template.
  async uploadWhatsAppTemplateHeaderMedia(file: File, format: 'IMAGE' | 'VIDEO' | 'DOCUMENT'): Promise<{ url: string; filename: string; contentType: string }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('format', format);

    const token = localStorage.getItem('auth_token');
    const headers: HeadersInit = {};
    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE_URL}/whatsapp-templates/header-media`, {
      method: 'POST',
      headers,
      body: formData,
    });

    const payload = await response.json().catch(() => ({}));
    if (!response.ok || !payload?.success) {
      throw new Error(payload?.message || 'Erro ao enviar arquivo do cabeçalho');
    }
    return payload.data;
  }

  async updateWhatsAppTemplateMapping(data: {
    accountId: string;
    templateName: string;
    language: string;
    variableOrder: string[];
  }): Promise<any> {
    return this.request('/whatsapp-templates/templates/mapping', {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  }

  async deleteWhatsAppTemplate(templateId: string, accountId: string): Promise<any> {
    return this.request(`/whatsapp-templates/templates/${templateId}?accountId=${accountId}`, {
      method: 'DELETE'
    });
  }

  async sendWhatsAppBulk(data: {
    accountId: string;
    recipients: Array<{ phone: string; name?: string; variables?: Record<string, string> }>;
    template: { name: string; language: string; components?: any[] };
  }): Promise<any> {
    return this.request('/whatsapp-templates/bulk-send', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async getWhatsAppBroadcasts(accountId: string): Promise<any> {
    return this.request(`/whatsapp-templates/broadcasts?accountId=${accountId}`);
  }

  async createWhatsAppBroadcast(data: {
    accountId: string;
    name: string;
    template: { name: string; language: string; components?: any[] };
    recipients?: Array<{ phone: string; name?: string; variables?: Record<string, string> }>;
    scheduledAt?: string;
  }): Promise<any> {
    return this.request('/whatsapp-templates/broadcasts', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async sendWhatsAppBroadcast(broadcastId: string): Promise<any> {
    return this.request(`/whatsapp-templates/broadcasts/${broadcastId}/send`, {
      method: 'POST'
    });
  }

  async deleteWhatsAppBroadcast(broadcastId: string): Promise<any> {
    return this.request(`/whatsapp-templates/broadcasts/${broadcastId}`, {
      method: 'DELETE'
    });
  }

  async getMarketingStudioDashboard(): Promise<{ success: boolean; data: MarketingStudioDashboard }> {
    return this.request('/marketing-studio/dashboard');
  }

  async getMarketingBrands(): Promise<{ success: boolean; brands: MarketingBrand[] }> {
    return this.request('/marketing-studio/brands');
  }

  async createMarketingBrand(data: MarketingBrandInput): Promise<{ success: boolean; brand: MarketingBrand }> {
    return this.request('/marketing-studio/brands', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async createMarketingBrandWithAgent(data: any): Promise<{ success: boolean; brand: MarketingBrand }> {
    return this.request('/marketing-studio/brands/agent', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async chatMarketingBrandAgent(data: any): Promise<{ success: boolean; reply: string; memory: any; draftBrandId?: string; progress: number; missing: string[]; shouldCreateBrand: boolean; brand?: MarketingBrand; debug?: any }> {
    return this.request('/marketing-studio/brands/agent/chat', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async updateMarketingBrand(id: string, data: Partial<MarketingBrandInput>): Promise<{ success: boolean; brand: MarketingBrand }> {
    return this.request(`/marketing-studio/brands/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async rewriteMarketingCampaignMatrix(id: string, instruction: string): Promise<{ success: boolean; brand: MarketingBrand }> {
    return this.request(`/marketing-studio/brands/${id}/campaign-matrix/rewrite`, {
      method: 'POST',
      body: JSON.stringify({ instruction }),
    });
  }

  async deleteMarketingBrand(id: string): Promise<{ success: boolean }> {
    return this.request(`/marketing-studio/brands/${id}`, {
      method: 'DELETE'
    });
  }

  async getMarketingBrandLooks(brandId: string): Promise<{ success: boolean; looks: any[] }> {
    return this.request(`/marketing-studio/brands/${brandId}/looks`);
  }

  async createMarketingBrandLook(brandId: string, data: any): Promise<{ success: boolean; look: any }> {
    return this.request(`/marketing-studio/brands/${brandId}/looks`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async updateMarketingBrandLook(brandId: string, lookId: string, data: any): Promise<{ success: boolean; look: any }> {
    return this.request(`/marketing-studio/brands/${brandId}/looks/${lookId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async deleteMarketingBrandLook(brandId: string, lookId: string): Promise<{ success: boolean }> {
    return this.request(`/marketing-studio/brands/${brandId}/looks/${lookId}`, {
      method: 'DELETE'
    });
  }

  async getMarketingInspirations(brandId: string): Promise<{ success: boolean; inspirations: any[] }> {
    return this.request(`/marketing-studio/brands/${brandId}/inspirations`);
  }

  async createMarketingInspiration(brandId: string, data: any): Promise<{ success: boolean; inspiration: any }> {
    return this.request(`/marketing-studio/brands/${brandId}/inspirations`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async updateMarketingInspiration(brandId: string, inspirationId: string, data: any): Promise<{ success: boolean; inspiration: any }> {
    return this.request(`/marketing-studio/brands/${brandId}/inspirations/${inspirationId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async deleteMarketingInspiration(brandId: string, inspirationId: string): Promise<{ success: boolean }> {
    return this.request(`/marketing-studio/brands/${brandId}/inspirations/${inspirationId}`, { method: 'DELETE' });
  }

  async discoverMarketingInspirations(brandId: string, data: any): Promise<{ success: boolean; apifyAvailable: boolean; candidates: any[]; message?: string }> {
    return this.request(`/marketing-studio/brands/${brandId}/inspirations/discover`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async createCreditPurchase(data: {
    credits: number;
    billingType: 'PIX' | 'CREDIT_CARD';
    idempotencyKey: string;
    buyer: { name: string; email: string; cpfCnpj: string; phone: string; postalCode?: string; addressNumber?: string; addressComplement?: string };
    creditCard?: { holderName: string; number: string; expiryMonth: string; expiryYear: string; ccv: string };
  }): Promise<{ success: boolean; data: CreditPurchase }> {
    return this.request('/credits/purchases', { method: 'POST', body: JSON.stringify(data) });
  }

  async getCreditPurchase(id: string): Promise<{ success: boolean; data: CreditPurchase }> {
    return this.request(`/credits/purchases/${encodeURIComponent(id)}`);
  }

  async getLatestPendingCreditPurchase(): Promise<{ success: boolean; data: CreditPurchase | null }> {
    return this.request('/credits/purchases/pending/latest');
  }
  async getMarketingAssets(brandId?: string): Promise<{ success: boolean; assets: MarketingCreativeAsset[] }> {
    const query = brandId ? `?brandId=${encodeURIComponent(brandId)}` : '';
    return this.request(`/marketing-studio/assets${query}`);
  }

  // Avatares
  async listMarketingAvatars(): Promise<{ success: boolean; avatars: any[] }> {
    return this.request('/marketing-studio/avatars');
  }

  async getMyAvatar(): Promise<{ success: boolean; avatar: any }> {
    return this.request('/marketing-studio/avatars/me');
  }
  async uploadBrandLogo(brandId: string, formData: FormData): Promise<{ success: boolean; logoUrl: string; brand: any }> {
    return this.requestRaw(`/marketing-studio/brands/${brandId}/logo`, { method: 'POST', body: formData });
  }

  async listProducts(): Promise<{ success: boolean; products: any[] }> {
    return this.request('/marketing-studio/products');
  }
  async listShopeeConnections(): Promise<{ success: boolean; data: any[] }> {
    return this.request('/shopee/connections');
  }
  async listMercadoLivreConnections(): Promise<{ success: boolean; data: any[] }> {
    return this.request('/mercado-livre/connections');
  }
  // `fields` e o mesmo formato do productSyncConfig.fields ({ price, name, description,
  // photos, videos }). Sem `fields` o backend usa os defaults dele - e o default de
  // `photos` la e TRUE, ou seja, o sync substitui as fotos do produto.
  async syncShopeeProducts(connectionId: string, fields?: any): Promise<MarketplaceProductSyncResult> {
    return this.request(`/marketing-studio/products/sync/shopee/${connectionId}`, { method: 'POST', ...(fields ? { body: JSON.stringify({ fields }) } : {}) });
  }
  async syncMercadoLivreProducts(connectionId: string, fields?: any): Promise<MarketplaceProductSyncResult> {
    return this.request(`/marketing-studio/products/sync/mercado-livre/${connectionId}`, { method: 'POST', ...(fields ? { body: JSON.stringify({ fields }) } : {}) });
  }
  async updateMarketplaceProductSyncConfig(marketplace: string, connectionId: string, config: any): Promise<{ success: boolean; connection: any }> {
    return this.request(`/marketing-studio/products/sync/${marketplace}/${connectionId}/config`, { method: 'PATCH', body: JSON.stringify(config) });
  }
  async generateProductAiFields(data: any): Promise<{ success: boolean; fields: { benefit: string; pain: string; dream: string; objection: string } }> {
    return this.request('/marketing-studio/products/ai-fields', { method: 'POST', body: JSON.stringify(data) });
  }
  async createProduct(data: any): Promise<{ success: boolean; product: any }> {
    return this.request('/marketing-studio/products', { method: 'POST', body: JSON.stringify(data) });
  }
  async updateProduct(productId: string, data: any): Promise<{ success: boolean; product: any }> {
    return this.request(`/marketing-studio/products/${productId}`, { method: 'PUT', body: JSON.stringify(data) });
  }
  async deleteProduct(productId: string): Promise<{ success: boolean }> {
    return this.request(`/marketing-studio/products/${productId}`, { method: 'DELETE' });
  }
  async uploadProductPhoto(productId: string, formData: FormData): Promise<{ success: boolean; photoUrl: string; product: any }> {
    return this.requestRaw(`/marketing-studio/products/${productId}/photos`, { method: 'POST', body: formData });
  }
  async deleteProductPhoto(productId: string, url: string): Promise<{ success: boolean; product: any }> {
    return this.request(`/marketing-studio/products/${productId}/photos`, { method: 'DELETE', body: JSON.stringify({ url }) });
  }

  // Orçamento do e-commerce pelo custo de provedor (estimativa; cobra o uso real por etapa).
  async estimateEcommercePipeline(kind: 'photo' | 'video', body: { steps?: any[]; pipelineId?: string }): Promise<{ success: boolean; estimate: { totalCredits: number; model: string; steps: { stepId: string | null; title: string; credits: number; llmCredits: number; lines: any[] }[] } }> {
    return this.request(kind === 'photo' ? '/ecommerce-photos/estimate' : '/ecommerce-videos/estimate', { method: 'POST', body: JSON.stringify(body) });
  }

  // ── Fotos E-commerce (pipeline de imagens de produto) ──
  async listEcommercePipelines(): Promise<{ success: boolean; pipelines: any[] }> {
    return this.request('/ecommerce-photos/pipelines');
  }
  async getEcommercePipeline(pipelineId: string): Promise<{ success: boolean; pipeline: any }> {
    return this.request(`/ecommerce-photos/pipelines/${pipelineId}`);
  }
  async createEcommercePipeline(data: any): Promise<{ success: boolean; pipeline: any }> {
    return this.request('/ecommerce-photos/pipelines', { method: 'POST', body: JSON.stringify(data) });
  }
  async cloneEcommercePipeline(pipelineId: string, name?: string): Promise<{ success: boolean; pipeline: any }> {
    return this.request(`/ecommerce-photos/pipelines/${pipelineId}/clone`, { method: 'POST', body: JSON.stringify({ name }) });
  }
  async updateEcommercePipeline(pipelineId: string, data: any): Promise<{ success: boolean; pipeline: any }> {
    return this.request(`/ecommerce-photos/pipelines/${pipelineId}`, { method: 'PUT', body: JSON.stringify(data) });
  }
  async deleteEcommercePipeline(pipelineId: string): Promise<{ success: boolean }> {
    return this.request(`/ecommerce-photos/pipelines/${pipelineId}`, { method: 'DELETE' });
  }
  async uploadEcommerceReference(formData: FormData): Promise<{ success: boolean; imageUrl: string }> {
    return this.requestRaw('/ecommerce-photos/upload-reference', { method: 'POST', body: formData });
  }
  async listEcommercePhotoJobs(): Promise<{ success: boolean; jobs: any[] }> {
    return this.request('/ecommerce-photos/jobs');
  }
  async getEcommercePhotoJob(jobId: string): Promise<{ success: boolean; job: any }> {
    return this.request(`/ecommerce-photos/jobs/${jobId}`);
  }
  async startEcommercePhotoJob(data: any): Promise<{ success: boolean; job: any }> {
    return this.request('/ecommerce-photos/jobs', { method: 'POST', body: JSON.stringify(data) });
  }
  async approveEcommercePhotoJob(jobId: string): Promise<{ success: boolean; job: any }> {
    return this.request(`/ecommerce-photos/jobs/${jobId}/approve`, { method: 'POST' });
  }
  async regenerateEcommercePhotoFirst(jobId: string, feedback?: string): Promise<{ success: boolean; job: any }> {
    return this.request(`/ecommerce-photos/jobs/${jobId}/regenerate-first`, { method: 'POST', body: JSON.stringify({ feedback }) });
  }
  async regenerateEcommercePhotoStep(jobId: string, stepId: string, feedback?: string, referenceImageUrl?: string): Promise<{ success: boolean; job: any }> {
    return this.request(`/ecommerce-photos/jobs/${jobId}/steps/${stepId}/regenerate`, { method: 'POST', body: JSON.stringify({ feedback, referenceImageUrl: referenceImageUrl || null }) });
  }
  // Reprocessa etapa que falhou em definitivo. O crédito da etapa falha já foi
  // estornado, então isto cobra de novo.
  async retryEcommercePhotoStep(jobId: string, stepId: string): Promise<{ success: boolean; job: any }> {
    return this.request(`/ecommerce-photos/jobs/${jobId}/steps/${stepId}/retry`, { method: 'POST' });
  }
  async resolveEcommercePhotoRegen(jobId: string, accept: boolean): Promise<{ success: boolean; job: any }> {
    return this.request(`/ecommerce-photos/jobs/${jobId}/resolve-regen`, { method: 'POST', body: JSON.stringify({ accept }) });
  }
  async deleteEcommercePhotoJob(jobId: string): Promise<{ success: boolean }> {
    return this.request(`/ecommerce-photos/jobs/${jobId}`, { method: 'DELETE' });
  }
  async applyEcommercePhotos(jobId: string, data: any): Promise<{ success: boolean; product: any; marketplaceResults: any[] }> {
    return this.request(`/ecommerce-photos/jobs/${jobId}/apply`, { method: 'POST', body: JSON.stringify(data) });
  }
  async listEcommerceMarketplaceTargets(productId: string): Promise<{ success: boolean; product: any; targets: any[] }> {
    return this.request(`/ecommerce-photos/products/${productId}/marketplace-targets`);
  }

  // ── Vídeos E-commerce (pipeline de vídeo de produto) ──
  // Motores HeyGen da etapa de avatar; créditos/segundo vêm da tabela de custos do super admin.
  async listEcommerceAvatarEngines(): Promise<{ success: boolean; engines: { id: 'avatar-iii' | 'avatar-iv'; label: string; rateKey: string; unit: string; creditsPerSecond: number | null; missing: boolean; isDefault: boolean }[] }> {
    return this.request('/ecommerce-videos/avatar-engines');
  }
  async listEcommerceVideoPipelines(): Promise<{ success: boolean; pipelines: any[] }> {
    return this.request('/ecommerce-videos/pipelines');
  }
  async getEcommerceVideoPipeline(pipelineId: string): Promise<{ success: boolean; pipeline: any }> {
    return this.request(`/ecommerce-videos/pipelines/${pipelineId}`);
  }
  async createEcommerceVideoPipeline(data: any): Promise<{ success: boolean; pipeline: any }> {
    return this.request('/ecommerce-videos/pipelines', { method: 'POST', body: JSON.stringify(data) });
  }
  async cloneEcommerceVideoPipeline(pipelineId: string, name?: string): Promise<{ success: boolean; pipeline: any }> {
    return this.request(`/ecommerce-videos/pipelines/${pipelineId}/clone`, { method: 'POST', body: JSON.stringify({ name }) });
  }
  async updateEcommerceVideoPipeline(pipelineId: string, data: any): Promise<{ success: boolean; pipeline: any }> {
    return this.request(`/ecommerce-videos/pipelines/${pipelineId}`, { method: 'PUT', body: JSON.stringify(data) });
  }
  async deleteEcommerceVideoPipeline(pipelineId: string): Promise<{ success: boolean }> {
    return this.request(`/ecommerce-videos/pipelines/${pipelineId}`, { method: 'DELETE' });
  }
  async uploadEcommerceVideoReference(formData: FormData): Promise<{ success: boolean; imageUrl: string }> {
    return this.requestRaw('/ecommerce-videos/upload-reference', { method: 'POST', body: formData });
  }
  async assistEcommerceVideoPrompt(stepKind: string, currentPrompt: string, direction?: string, references?: string[], stepTitle?: string): Promise<{ success: boolean; prompt: string }> {
    return this.request('/ecommerce-videos/assist-prompt', { method: 'POST', body: JSON.stringify({ stepKind, currentPrompt, direction, references, stepTitle }) });
  }
  async assistEcommercePhotoPrompt(data: { stepKind: string; currentPrompt: string; direction?: string; reference?: string; useReference?: boolean; stepTitle?: string }): Promise<{ success: boolean; prompt: string }> {
    return this.request('/ecommerce-photos/assist-prompt', { method: 'POST', body: JSON.stringify(data) });
  }
  async listEcommerceVideoJobs(): Promise<{ success: boolean; jobs: any[] }> {
    return this.request('/ecommerce-videos/jobs');
  }
  async getEcommerceVideoJob(jobId: string): Promise<{ success: boolean; job: any }> {
    return this.request(`/ecommerce-videos/jobs/${jobId}`);
  }
  async startEcommerceVideoJob(data: any): Promise<{ success: boolean; job: any }> {
    return this.request('/ecommerce-videos/jobs', { method: 'POST', body: JSON.stringify(data) });
  }
  async approveEcommerceVideoJob(jobId: string): Promise<{ success: boolean; job: any }> {
    return this.request(`/ecommerce-videos/jobs/${jobId}/approve`, { method: 'POST' });
  }
  async regenerateEcommerceVideoFirst(jobId: string, feedback?: string): Promise<{ success: boolean; job: any }> {
    return this.request(`/ecommerce-videos/jobs/${jobId}/regenerate-first`, { method: 'POST', body: JSON.stringify({ feedback }) });
  }
  async regenerateEcommerceVideoStep(jobId: string, stepId: string, feedback?: string, referenceImageUrl?: string): Promise<{ success: boolean; job: any }> {
    return this.request(`/ecommerce-videos/jobs/${jobId}/steps/${stepId}/regenerate`, { method: 'POST', body: JSON.stringify({ feedback, referenceImageUrl: referenceImageUrl || null }) });
  }
  // Reprocessa etapa que falhou em definitivo. O crédito da etapa falha já foi
  // estornado, então isto cobra de novo (cena ou avatar; frame e merge não custam).
  async retryEcommerceVideoStep(jobId: string, stepId: string): Promise<{ success: boolean; job: any }> {
    return this.request(`/ecommerce-videos/jobs/${jobId}/steps/${stepId}/retry`, { method: 'POST' });
  }
  async resolveEcommerceVideoRegen(jobId: string, accept: boolean): Promise<{ success: boolean; job: any; canRemerge: boolean }> {
    return this.request(`/ecommerce-videos/jobs/${jobId}/resolve-regen`, { method: 'POST', body: JSON.stringify({ accept }) });
  }
  async remergeEcommerceVideo(jobId: string): Promise<{ success: boolean; job: any }> {
    return this.request(`/ecommerce-videos/jobs/${jobId}/remerge`, { method: 'POST' });
  }

  // mode: 'instant' (trocou a URL na hora) | 'remerge' (remontando de verdade) | 'noop'
  async setEcommerceVideoOverlays(jobId: string, enabled: boolean): Promise<{ success: boolean; mode: string; job: any }> {
    return this.request(`/ecommerce-videos/jobs/${jobId}/overlays`, { method: 'POST', body: JSON.stringify({ enabled }) });
  }
  async saveEcommerceVideoToProduct(jobId: string, stepId: string, productId?: string): Promise<{ success: boolean; video: any }> {
    return this.request(`/ecommerce-videos/jobs/${jobId}/steps/${stepId}/save-to-product`, { method: 'POST', body: JSON.stringify({ productId }) });
  }
  async sendEcommerceVideoToMarketplace(jobId: string, stepId: string, productId?: string, marketplaceTargets?: any[]): Promise<{ success: boolean; productId: string; results: any[] }> {
    return this.request(`/ecommerce-videos/jobs/${jobId}/steps/${stepId}/send-to-marketplace`, { method: 'POST', body: JSON.stringify({ productId, marketplaceTargets }) });
  }
  async listEcommerceProductVideos(productId: string): Promise<{ success: boolean; videos: any[] }> {
    return this.request(`/ecommerce-videos/products/${productId}/videos`);
  }
  async deleteEcommerceProductVideo(videoId: string): Promise<{ success: boolean }> {
    return this.request(`/ecommerce-videos/product-videos/${videoId}`, { method: 'DELETE' });
  }
  async deleteEcommerceVideoJob(jobId: string): Promise<{ success: boolean }> {
    return this.request(`/ecommerce-videos/jobs/${jobId}`, { method: 'DELETE' });
  }

  async uploadAvatarPhoto(formData: FormData): Promise<{ success: boolean; avatar: any }> {
    return this.requestRaw('/marketing-studio/avatars/setup', { method: 'POST', body: formData });
  }
  async uploadAvatarVoice(avatarId: string, formData: FormData): Promise<{ success: boolean; processing: boolean; message?: string; voiceId?: string; avatar?: any }> {
    return this.requestRaw(`/marketing-studio/avatars/${avatarId}/voice`, { method: 'POST', body: formData });
  }
  async uploadAvatarLook(avatarId: string, formData: FormData): Promise<{ success: boolean; look: any }> {
    return this.requestRaw(`/marketing-studio/avatars/${avatarId}/looks/upload`, { method: 'POST', body: formData });
  }
  async updateMarketingAvatar(avatarId: string, data: any): Promise<{ success: boolean; avatar: any; processing?: boolean; message?: string }> {
    return this.request(`/marketing-studio/avatars/${avatarId}`, { method: 'PUT', body: JSON.stringify(data) });
  }
  async deleteMarketingAvatar(avatarId: string): Promise<{ success: boolean }> {
    return this.request(`/marketing-studio/avatars/${avatarId}`, { method: 'DELETE' });
  }
  async updateMarketingAvatarLook(avatarId: string, lookId: string, data: any): Promise<{ success: boolean; look: any }> {
    return this.request(`/marketing-studio/avatars/${avatarId}/looks/${lookId}`, { method: 'PUT', body: JSON.stringify(data) });
  }
  async deleteMarketingAvatarLook(avatarId: string, lookId: string): Promise<{ success: boolean }> {
    return this.request(`/marketing-studio/avatars/${avatarId}/looks/${lookId}`, { method: 'DELETE' });
  }
  // Painel de referência do avatar (4 vistas + quadro 2x2)
  async getAvatarReferencePlate(avatarId: string): Promise<{ success: boolean; referencePlateUrl: string | null; referencePlateViews: any[] | null; referencePlateStatus: string | null }> {
    return this.request(`/marketing-studio/avatars/${avatarId}/reference-plate`);
  }
  async generateAvatarReferencePlate(avatarId: string): Promise<{ success: boolean; processing?: boolean; message?: string; referencePlateUrl: string | null; referencePlateViews: any[] | null; referencePlateStatus: string | null }> {
    return this.request(`/marketing-studio/avatars/${avatarId}/reference-plate`, { method: 'POST' });
  }
  // Refazer uma vista aceita ajuste opcional: texto do que saiu errado e/ou uma foto
  // de referência adicional (URL vinda do mesmo upload de referência do pipeline).
  async regenerateAvatarReferencePlateView(avatarId: string, view: string, params?: { feedback?: string | null; referenceImageUrl?: string | null }): Promise<{ success: boolean; processing?: boolean; message?: string; referencePlateUrl: string | null; referencePlateViews: any[] | null; referencePlateStatus: string | null }> {
    return this.request(`/marketing-studio/avatars/${avatarId}/reference-plate/views/${view}`, {
      method: 'POST',
      body: JSON.stringify({
        feedback: params?.feedback || null,
        referenceImageUrl: params?.referenceImageUrl || null,
      }),
    });
  }
  // Video Editor
  // Um vídeo-fonte por requisição, com progresso. XHR em vez de fetch porque
  // fetch não expõe progresso de upload. Erros vêm no mesmo formato do request().
  uploadVideoEditorSource(
    file: File,
    meta: { mode: string; index: number },
    onProgress?: (fraction: number) => void,
  ): Promise<{ success: boolean; upload: { url: string; filename: string; size: number } }> {
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append('mode', meta.mode);
      formData.append('index', String(meta.index));
      formData.append('video', file);
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_BASE_URL}/marketing-studio/video-editor/uploads`);
      const token = localStorage.getItem('auth_token');
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      const selectedTenantId = localStorage.getItem('superadmin_selected_tenant') || localStorage.getItem('selected_tenant_id');
      if (selectedTenantId) xhr.setRequestHeader('X-Tenant-Id', selectedTenantId);
      xhr.upload.onprogress = (e) => {
        if (onProgress && e.lengthComputable) onProgress(e.loaded / e.total);
      };
      xhr.onerror = () => reject(new Error(`Falha de rede ao enviar "${file.name}". Verifique a conexão e tente de novo.`));
      xhr.ontimeout = () => reject(new Error(`Tempo esgotado ao enviar "${file.name}".`));
      xhr.onload = () => {
        let body: any = {};
        try { body = JSON.parse(xhr.responseText || '{}'); } catch { body = { error: 'Erro desconhecido' }; }
        if (xhr.status >= 200 && xhr.status < 300) return resolve(body);
        if (xhr.status === 402 || body.code === 'INSUFFICIENT_CREDITS') {
          const creditsError = new Error(body.message || body.error || 'Créditos insuficientes.');
          (creditsError as any).isCreditsError = true;
          (creditsError as any).required = body.required;
          (creditsError as any).balance = body.balance;
          (creditsError as any).featureKey = body.featureKey;
          return reject(creditsError);
        }
        reject(new Error(body.error || body.message || `HTTP ${xhr.status}`));
      };
      xhr.send(formData);
    });
  }

  // Cria o job a partir das referências devolvidas por uploadVideoEditorSource.
  async startVideoEditorJob(payload: Record<string, unknown>): Promise<{ success: boolean; job: any }> {
    return this.request('/marketing-studio/video-editor/jobs', { method: 'POST', body: JSON.stringify(payload) });
  }

  async listVideoEditorJobs(brandId?: string, mode?: string): Promise<{ success: boolean; jobs: any[] }> {
    const params = new URLSearchParams();
    if (brandId) params.set('brandId', brandId);
    if (mode) params.set('mode', mode);
    const q = params.toString() ? `?${params.toString()}` : '';
    return this.request(`/marketing-studio/video-editor/jobs${q}`);
  }

  async getVideoEditorJob(jobId: string): Promise<{ success: boolean; job: any }> {
    return this.request(`/marketing-studio/video-editor/jobs/${jobId}`);
  }

  async deleteVideoEditorJob(jobId: string): Promise<{ success: boolean }> {
    return this.request(`/marketing-studio/video-editor/jobs/${jobId}`, { method: 'DELETE' });
  }

  async downloadVideoEditorArtifact(jobId: string, artifact: string = 'final'): Promise<Blob> {
    const token = localStorage.getItem('auth_token');
    const selectedTenantId = localStorage.getItem('superadmin_selected_tenant') || localStorage.getItem('selected_tenant_id');
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    if (selectedTenantId) headers['X-Tenant-Id'] = selectedTenantId;
    const response = await fetch(`${API_BASE_URL}/marketing-studio/video-editor/jobs/${jobId}/download?artifact=${encodeURIComponent(artifact)}`, {
      headers,
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Erro ao baixar arquivo' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }
    return response.blob();
  }

  // Onda sonora / tira de miniaturas de um asset, para desenhar dentro do bloco
  // na régua de tempo. Vem como imagem autenticada, então o caminho é fetch +
  // objectURL — <img src> direto não carrega o cabeçalho de autorização.
  async getVideoEditorAssetGraphics(jobId: string, assetId: string, kind: 'waveform' | 'filmstrip'): Promise<string> {
    const token = localStorage.getItem('auth_token');
    const selectedTenantId = localStorage.getItem('superadmin_selected_tenant') || localStorage.getItem('selected_tenant_id');
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    if (selectedTenantId) headers['X-Tenant-Id'] = selectedTenantId;
    const response = await fetch(
      `${API_BASE_URL}/marketing-studio/video-editor/jobs/${jobId}/media/${encodeURIComponent(assetId)}/graphics?kind=${kind}`,
      { headers },
    );
    // 204 = a mídia não tem o que desenhar (vídeo sem áudio). Não é erro e não
    // adianta tentar de novo — devolve vazio para o chamador marcar como resolvido.
    if (response.status === 204) return '';
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return URL.createObjectURL(await response.blob());
  }

  async getVideoEditorProject(jobId: string, target: string = 'final'): Promise<VideoEditorProjectResponse> {
    return this.request(`/marketing-studio/video-editor/jobs/${jobId}/project?target=${encodeURIComponent(target)}`);
  }

  // purpose: 'preview' (default, URL curta) ou 'publish' — a URL entregue à rede social
  // precisa sobreviver até a hora do post agendado.
  async getVideoEditorAssetUrl(jobId: string, assetId: string, purpose?: 'preview' | 'publish' | 'download'): Promise<{
    success: boolean;
    asset: { id: string; name: string; kind: 'video' | 'image'; url: string; expiresAt?: string; duration: number };
  }> {
    const query = purpose === 'publish' || purpose === 'download' ? `?purpose=${purpose}` : '';
    return this.request(`/marketing-studio/video-editor/jobs/${jobId}/media/${encodeURIComponent(assetId)}/url${query}`);
  }

  async uploadVideoEditorAssets(jobId: string, files: File[]): Promise<{ success: boolean; assets: VideoEditorAsset[] }> {
    const formData = new FormData();
    files.forEach(file => formData.append('assets', file));
    return this.requestRaw(`/marketing-studio/video-editor/jobs/${jobId}/assets`, {
      method: 'POST',
      body: formData,
    });
  }

  async deleteVideoEditorAsset(jobId: string, assetId: string): Promise<{ success: boolean }> {
    return this.request(`/marketing-studio/video-editor/jobs/${jobId}/assets/${encodeURIComponent(assetId)}`, {
      method: 'DELETE',
    });
  }

  async listVideoEditorSoundEffects(): Promise<{ success: boolean; soundEffects: VideoEditorSoundEffect[] }> {
    return this.request('/marketing-studio/video-editor/sound-effects');
  }

  // Esta empresa consegue usar trilha do catalogo? Consultado ANTES de oferecer a
  // opcao: so a Zernio sabe responder, porque o tipo de login nao vem na conta.
  async getInstagramMusicEligibility(): Promise<{
    success: boolean; eligible: boolean; reason?: string | null; username?: string | null; uncertain?: boolean;
  }> {
    return this.request('/marketing-studio/video-editor/music/eligibility');
  }

  // Ajusta volume/liga-desliga da trilha sem abrir o editor.
  async updateVideoEditorMusic(jobId: string, patch: { volume?: number; enabled?: boolean }): Promise<{
    success: boolean; musicSelection: any;
  }> {
    return this.request(`/marketing-studio/video-editor/jobs/${jobId}/music`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  }

  // Catalogo de trilha do Instagram para o editor. Sem `q`, volta o que esta em alta.
  async listVideoEditorMusic(params: { q?: string; audioType?: 'music' | 'original_sounds' } = {}): Promise<{
    success: boolean;
    tracks: VideoEditorMusicTrack[];
    trending?: boolean;
    unavailable?: string | null;
  }> {
    const query = new URLSearchParams();
    query.set('audioType', params.audioType || 'music');
    if (params.q) query.set('q', params.q);
    return this.request(`/marketing-studio/video-editor/music/catalog?${query.toString()}`);
  }

  async downloadVideoEditorSoundEffect(soundEffectId: string): Promise<Blob> {
    const token = localStorage.getItem('auth_token');
    const selectedTenantId = localStorage.getItem('superadmin_selected_tenant') || localStorage.getItem('selected_tenant_id');
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    if (selectedTenantId) headers['X-Tenant-Id'] = selectedTenantId;
    const response = await fetch(`${API_BASE_URL}/marketing-studio/video-editor/sound-effects/${encodeURIComponent(soundEffectId)}`, { headers });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Efeito sonoro indisponível' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }
    return response.blob();
  }

  // O peso e o itálico viajam no pedido: uma variante só obriga o navegador a
  // falsificar negrito e itálico, e o preview deixa de parecer com o vídeo.
  async downloadVideoEditorFont(
    jobId: string,
    target: string,
    family: string,
    weight = 400,
    italic = false,
  ): Promise<Blob> {
    const token = localStorage.getItem('auth_token');
    const selectedTenantId = localStorage.getItem('superadmin_selected_tenant') || localStorage.getItem('selected_tenant_id');
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    if (selectedTenantId) headers['X-Tenant-Id'] = selectedTenantId;
    const params = new URLSearchParams({ target, family, weight: String(weight), italic: italic ? '1' : '0' });
    const response = await fetch(`${API_BASE_URL}/marketing-studio/video-editor/jobs/${jobId}/font?${params.toString()}`, { headers });
    if (!response.ok) throw new Error(`Fonte ${family} ${weight}${italic ? ' itálico' : ''} indisponível no projeto.`);
    return response.blob();
  }

  async saveVideoEditorProject(
    jobId: string,
    target: string,
    project: VideoEditorProject,
    baseRevision: number,
  ): Promise<SaveVideoEditorProjectResponse> {
    return this.request(`/marketing-studio/video-editor/jobs/${jobId}/project?target=${encodeURIComponent(target)}`, {
      method: 'PUT',
      body: JSON.stringify({ target, project, baseRevision }),
    });
  }

  async renderVideoEditorProject(jobId: string, target: string): Promise<RenderVideoEditorProjectResponse> {
    return this.request(`/marketing-studio/video-editor/jobs/${jobId}/render`, {
      method: 'POST',
      body: JSON.stringify({ target }),
    });
  }

  // ── Editor edvid (Fase 1 corte → Estilo → Fase 2 visual) ──────────────────
  // Contrato em types/videoEdit.ts. Toda mutação devolve o job atualizado; as
  // que disparam render deixam output.edvid.phase em 'cutting'/'visual_rendering'
  // e o editor passa a consultar GET /jobs/:id até o render terminar. Conflito de
  // revisão volta como HTTP 409 (error.status === 409) com o estado atual no corpo.
  async getEdvidJob(jobId: string): Promise<{ success: boolean; job: EdvidJobRecord }> {
    return this.request(`/marketing-studio/video-editor/jobs/${jobId}`);
  }

  async saveEdvidCut(jobId: string, data: { ranges: EdlRange[]; baseRevision: number }): Promise<{ success: boolean; job: EdvidJobRecord }> {
    return this.request(`/marketing-studio/video-editor/jobs/${jobId}/cut`, { method: 'PUT', body: JSON.stringify(data) });
  }

  async recutEdvid(jobId: string, data: { note?: string } = {}): Promise<{ success: boolean; job: EdvidJobRecord }> {
    return this.request(`/marketing-studio/video-editor/jobs/${jobId}/recut`, { method: 'POST', body: JSON.stringify(data) });
  }

  async applyEdvidStyle(jobId: string, style: StyleSetup): Promise<{ success: boolean; job: EdvidJobRecord }> {
    return this.request(`/marketing-studio/video-editor/jobs/${jobId}/style`, { method: 'POST', body: JSON.stringify({ style }) });
  }

  async saveEdvidVisual(jobId: string, data: { props: EdvidVisualState['props']; baseRevision: number }): Promise<{ success: boolean; job: EdvidJobRecord }> {
    return this.request(`/marketing-studio/video-editor/jobs/${jobId}/visual`, { method: 'PUT', body: JSON.stringify(data) });
  }

  async applyEdvidNotes(jobId: string, notes: EdvidNote[]): Promise<{ success: boolean; job: EdvidJobRecord }> {
    return this.request(`/marketing-studio/video-editor/jobs/${jobId}/notes`, { method: 'POST', body: JSON.stringify({ notes }) });
  }

  /** 3–4 opções de headline para a aba Estilo (uma chamada de IA, ~5s; não dispara render). */
  async getEdvidHookOptions(jobId: string): Promise<{ success: boolean; options: string[] }> {
    return this.request(`/marketing-studio/video-editor/jobs/${jobId}/hooks`, { method: 'POST', body: JSON.stringify({}) });
  }

  async getPostingAccounts(): Promise<{ success: boolean; data: any[] }> {
    return this.request('/posting/accounts');
  }

  async createPost(data: {
    content: string;
    platforms: { platform: string; accountId: string; customContent?: string; platformSpecificData?: Record<string, any> }[];
    mediaItems?: { url: string; type: string }[];
    publishNow?: boolean;
    scheduledFor?: string;
    timezone?: string;
    firstComment?: string;
    automationSpec?: any;
  }): Promise<any> {
    return this.request('/posting/create', { method: 'POST', body: JSON.stringify(data) });
  }

  async updatePost(postId: string, data: { content?: string; scheduledFor?: string; publishNow?: boolean }): Promise<any> {
    return this.request(`/posting/posts/${postId}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  // ===== Cut App (importacao de produtos + origem dos precos) =====
  async getCutappConnection(): Promise<any> {
    return this.request('/cutapp/connection');
  }

  // O backend expoe um unico PUT idempotente (upsert) para a conexao: criar e
  // atualizar sao a mesma rota. As duas assinaturas ficam para nao mexer nas telas.
  async createCutappConnection(data: CutappConnectionInput): Promise<any> {
    return this.request('/cutapp/connection', { method: 'PUT', body: JSON.stringify(data) });
  }

  async updateCutappConnection(data: CutappConnectionInput): Promise<any> {
    return this.request('/cutapp/connection', { method: 'PUT', body: JSON.stringify(data) });
  }

  async testCutappConnection(data: { baseUrl?: string; apiKey?: string; catalogIds?: string[] } = {}): Promise<any> {
    return this.request('/cutapp/test', { method: 'POST', body: JSON.stringify(data) });
  }

  async runCutappImport(data: { catalogIds?: string[] } = {}): Promise<any> {
    return this.request('/cutapp/import', { method: 'POST', body: JSON.stringify(data) });
  }

  async runCutappSyncPrices(data: Record<string, unknown> = {}): Promise<any> {
    return this.request('/cutapp/sync-prices', { method: 'POST', body: JSON.stringify(data) });
  }

  async getCutappPriceMappings(): Promise<any> {
    return this.request('/cutapp/price-mappings');
  }

  // O backend grava UM mapeamento por marketplace (`PUT /price-mappings/:marketplace`),
  // porque a validacao do §6.1 e por marketplace: habilitar sem % de desconto tem que
  // falhar so naquele marketplace, nao no lote inteiro. Salva em sequencia e devolve o
  // resultado por marketplace, com o erro individual preservado.
  async saveCutappPriceMappings(mappings: CutappPriceMapping[]): Promise<any> {
    const results: Array<{ marketplace: string; success: boolean; mapping?: unknown; message?: string }> = [];
    for (const mapping of mappings) {
      const { marketplace, ...body } = mapping as CutappPriceMapping & Record<string, unknown>;
      try {
        const res: any = await this.request(`/cutapp/price-mappings/${encodeURIComponent(String(marketplace))}`, {
          method: 'PUT',
          body: JSON.stringify(body),
        });
        results.push({ marketplace: String(marketplace), success: true, mapping: res?.mapping ?? res });
      } catch (error: any) {
        results.push({ marketplace: String(marketplace), success: false, message: error?.message || 'Falha ao salvar mapeamento.' });
      }
    }
    const falhas = results.filter(r => !r.success);
    if (falhas.length > 0) {
      throw new Error(falhas.map(f => `${f.marketplace}: ${f.message}`).join(' | '));
    }
    return { success: true, results, mappings: results.map(r => r.mapping) };
  }

  async getCutappPreviewPrices(): Promise<any> {
    return this.request('/cutapp/preview-prices');
  }
}

export const apiService = new ApiService();
export const api = apiService;
